/**
 * Change a live game through one server-owned composed revision
 * (`advanceComposedRevision`): "Change your game" for a live composed game,
 * "Rebuild with the new builder" for a live generic game. Every change runs,
 * automatically: the page starts the run, follows its durable server continuation,
 * shows its progress, then the ordinary build of the new version
 * (DeejWorkshop) and, once that version is live, what changed and which art
 * was kept or redrawn. A failed build of the accepted change is repaired from
 * its saved plan (requestBuild's composed retry). On mount it reads the saved
 * run and picks up where it is.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ComposedRevisionViewV1, Creation, CreationVersion } from '@djgg/creations';
import { COMPOSED_RULES_EDITING_UNAVAILABLE } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { journeyStallWatch } from './journeyStall.js';
import { WizardStoryReel } from './wizard/WizardStoryReel.js';
import { AdminErrorDetail, ComposedCreateProgress, ComposedRetryLimit, ComposedRunFailure } from './wizard/ComposedCreateProgress.js';
import { BUILD_REPAIR_FAILED_MESSAGE, CHANGE_BUSY_MESSAGE, CHANGE_FAILED_MESSAGE, RETRY_LIMIT_CHANGE_MESSAGE, REVISION_START_FAILED_MESSAGE, plainThrownError } from './wizard/creatorCopy.js';
import { plainPlanText } from '@djgg/creations';
import { composedPlanTemplateTitleV1 } from './wizard/composedCreate.js';
import {
  COMPOSED_REVISION_KEPT_REASON, composedChangeEndedV1, composedRevisionBuildVersionV1, composedRevisionCallErrorV1, composedRevisionStages,
  type ComposedRevisionCallErrorV1,
} from './composedRevision.js';
import { composedRetryBuildRequest, newBuildCommandId } from './buildCommands.js';

/** How long the page waits before reading again a stage the server is still running. */
export const COMPOSED_REVISION_POLL_MS = 5_000;

const pause = () => new Promise(resolve => window.setTimeout(resolve, COMPOSED_REVISION_POLL_MS));

export interface PendingWorkshopSelection {
  slot: string;
  variantId: string;
}

export function ComposedRevisionPanel({ creation, mode, latest, onPreparingChange, onRunChange, pendingSelections = [], creationFilmsEnabled = true }: {
  onPreparingChange?: (active: boolean) => void;
  onRunChange?: (run: ComposedRevisionViewV1 | null) => void;
  pendingSelections?: PendingWorkshopSelection[];
  creationFilmsEnabled?: boolean;
  creation: Creation & { id: string };
  /** `change`: a live composed game; `rebuild`: a live generic game moved onto the composed builder. */
  mode: 'change' | 'rebuild';
  /** The creation's latest version: the revision's build once it is accepted. */
  latest: (CreationVersion & { id: string }) | null;
}) {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const rebuild = mode === 'rebuild';
  const [request, setRequest] = useState('');
  const [run, setRun] = useState<ComposedRevisionViewV1 | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [failure, setFailure] = useState<(ComposedRevisionCallErrorV1 & { plain: string }) | null>(null);
  const [loadError, setLoadError] = useState('');
  const [profileUnsupported, setProfileUnsupported] = useState(false);
  const [buildRetryError, setBuildRetryError] = useState<{ plain: string; detail: string } | null>(null);
  const [repairing, setRepairing] = useState(false);
  // The kind of run on this page: a rebuilt game becomes a composed game once it is live.
  const [runRebuild, setRunRebuild] = useState(rebuild);
  const inFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { onRunChange?.(run); }, [run, onRunChange]);

  const attributed = (attributeTo: string | undefined) => (attributeTo ? { attributeTo } : {});
  async function read(): Promise<ComposedRevisionViewV1 | null> {
    return (await services.callable.advanceComposedRevision({ creationId: creation.id })).run;
  }
  /** Follow the run to its accepted build or its failure: read while the server advances stages, and wake a
   * ready stage the queue has left waiting. */
  async function drive(start: ComposedRevisionViewV1, attributeTo: string | undefined): Promise<void> {
    const stalled = journeyStallWatch();
    let current: ComposedRevisionViewV1 | null = start;
    while (current && alive.current) {
      setRun(current);
      if (current.status === 'accepted' || current.status === 'failed') return;
      const wake: boolean = stalled(`${current.status}:${current.stage}:${current.stepsDone}`) && current.status === 'ready';
      await pause();
      if (!alive.current) return;
      const runId: string = current.runId;
      const next: ComposedRevisionViewV1 | null = wake ? await wakeStage(runId, attributeTo) : await read();
      if (next?.runId !== runId) { if (alive.current) setRun(next); return; }
      current = next;
    }
  }
  /** A delivery that claimed the stage first wins; follow it. */
  async function wakeStage(runId: string, attributeTo: string | undefined): Promise<ComposedRevisionViewV1 | null> {
    try {
      return (await services.callable.advanceComposedRevision({ creationId: creation.id, runId, ...attributed(attributeTo) })).run;
    } catch (error) {
      const kind = composedRevisionCallErrorV1(error).kind;
      if (kind !== 'in-progress' && kind !== 'stale') throw error;
      return read();
    }
  }
  /** Run `first` (a start, a resume or a retry), then follow the run it returns. A change already in progress, or
   * a run this page no longer holds, is read again and followed instead. */
  async function session(attributeTo: string | undefined, first: () => Promise<ComposedRevisionViewV1 | null>): Promise<void> {
    let start: ComposedRevisionViewV1 | null;
    try {
      start = await first();
    } catch (error) {
      const kind = composedRevisionCallErrorV1(error).kind;
      if (kind !== 'in-progress' && kind !== 'stale') throw error;
      start = await read();
    }
    if (start && alive.current) await drive(start, attributeTo);
  }
  async function exclusive(work: () => Promise<void>): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setAdvancing(true);
    setFailure(null);
    try {
      await work();
    } catch (error) {
      if (!alive.current) return;
      const refused = composedRevisionCallErrorV1(error);
      if (refused.kind === 'profile-unsupported') setProfileUnsupported(true);
      else setFailure({ ...refused, plain: plainThrownError(error, REVISION_START_FAILED_MESSAGE) });
    } finally {
      inFlight.current = false;
      if (alive.current) setAdvancing(false);
    }
  }
  useEffect(() => {
    void exclusive(async () => {
      let saved: ComposedRevisionViewV1 | null;
      try {
        saved = await read();
      } catch {
        if (alive.current) setLoadError('We couldn’t load this game’s saved change. Reload the page to see its progress.');
        return;
      }
      if (!alive.current || !saved) return;
      setRun(saved);
      if (saved.status === 'ready' || saved.status === 'claimed') {
        const resumed = saved;
        await runAttributed(creation, attributeTo => session(attributeTo, async () => resumed.status === 'ready'
          ? (await services.callable.advanceComposedRevision({ creationId: creation.id, runId: resumed.runId, ...attributed(attributeTo) })).run : resumed));
      }
    });
  }, []);
  function start(): void {
    const text = request.trim();
    setRun(null);
    setLoadError('');
    setRunRebuild(rebuild);
    void exclusive(() => runAttributed(creation, attributeTo => session(attributeTo, async () =>
      (await services.callable.advanceComposedRevision({
        // A generic game is converted as it is; a composed game changes by the creator's request.
        creationId: creation.id, request: rebuild ? null : text, ...attributed(attributeTo),
      })).run)));
  }
  /** Retry a conversion's failed or lost stage; a stage that may have paid a provider retries as an approved paid retry. */
  function retry(saved: ComposedRevisionViewV1): void {
    void exclusive(() => runAttributed(creation, attributeTo => session(attributeTo, async () =>
      (await services.callable.advanceComposedRevision({
        creationId: creation.id, runId: saved.runId, ...attributed(attributeTo),
        ...(saved.stage !== 'build' ? { retryPaidStage: true } : {}),
        ...(failure?.kind === 'conversion-replan' ? { replan: true } : {}),
      })).run)));
  }
  /** Build the accepted run's failed version again through requestBuild's composed retry; the run stays accepted
   * and the retry's progress is the new version's, which this page and the workshop read. */
  function retryBuild(failed: CreationVersion): void {
    const commandId = newBuildCommandId();
    setBuildRetryError(null);
    void exclusive(async () => {
      setRepairing(true);
      try {
        await runAttributed(creation, attributeTo => services.callable.requestBuild(
          composedRetryBuildRequest(creation, failed, commandId, attributeTo)).then(() => undefined));
      } catch (error) {
        if (alive.current) setBuildRetryError({ plain: plainThrownError(error, BUILD_REPAIR_FAILED_MESSAGE), detail: error instanceof Error ? error.message : String(error) });
      } finally {
        if (alive.current) setRepairing(false);
      }
    });
  }
  function startOver(): void {
    setRun(null);
    setFailure(null);
  }

  const building = creation.buildState === 'building';
  // A run that can't continue, or a change that has ended: only a new change can follow.
  const stopped = failure?.kind === 'base-changed' || failure?.kind === 'no-change' ? failure.message
    : run?.failure?.code === 'base-changed' || run?.failure?.code === 'no-change' ? run.failure.message : null;
  const changeFailed = composedChangeEndedV1(run, failure);
  const ended = !advancing && (stopped !== null || changeFailed);
  // An accepted run's build (or its retry) stays on show until a later build replaces it.
  const builtVersion = composedRevisionBuildVersionV1(run, creation, latest);
  const buildCurrent = builtVersion !== null;
  const buildVersion = buildCurrent && latest?.n === builtVersion ? latest : null;
  const live = buildCurrent && (buildVersion?.buildStatus === 'live' || creation.activeVersion === builtVersion);
  const buildFailed = buildVersion?.buildStatus === 'failed' ? buildVersion : null;
  // A failed build of the accepted change is built again only while the live game is the one it changed.
  const buildRetryable = buildFailed !== null && creation.activeVersion === run?.base.version;
  const showProgress = !profileUnsupported && !ended && run !== null && (run.status !== 'accepted' || buildCurrent);
  const stageError = advancing || ended ? null
    : failure?.message ?? (run?.status === 'failed' ? run.failure?.message ?? 'This stage failed.' : null);

  const preparing = !!run && run.status !== 'accepted';
  useEffect(() => { onPreparingChange?.(preparing); return () => onPreparingChange?.(false); }, [preparing, onPreparingChange]);

  return <section className="composed-revision" data-testid="composed-revision" data-mode={mode}
    aria-label={rebuild ? 'Rebuild with the new builder' : 'Change your game'}>
    {attributionModal}
    {!preparing && !building && <h3>{rebuild ? 'Rebuild with the new builder' : 'Change your game'}</h3>}
    {rebuild
      ? <p data-testid="revision-rebuild-warning">This builds a new version of your game from the platform’s reusable
        parts. When it is ready it becomes the live version. You can switch back to the current version
        (v{creation.activeVersion}) from <Link to="?tab=overview">Overview</Link>.</p>
      : null}
    {loadError && <p role="alert" data-testid="revision-load-error">{loadError}</p>}
    {profileUnsupported
      ? <div role="status" data-testid="revision-profile-unsupported">
        <p>{COMPOSED_RULES_EDITING_UNAVAILABLE}</p>
        <Link className="btn" to="?tab=assets">Change art in Assets</Link>
      </div>
      : !preparing && !building && !(buildCurrent && !live) && <form onSubmit={event => { event.preventDefault(); start(); }}>
        {pendingSelections.length > 0 && <div className="revision-pending-choices" data-testid="revision-pending-choices">
          <h4>Your saved artwork choices</h4>
          <p className="muted">When this update goes live, each choice moves to the new version if its slot still exists. If a slot is gone, the workshop tells you which one. If the update fails, nothing moves.</p>
          <ul>{pendingSelections.map(item => <li key={`${item.slot}:${item.variantId}`}><strong>{item.slot.replace(/^[^:]+:/, '').replaceAll('-', ' ')}</strong></li>)}</ul>
        </div>}
        {!rebuild && <label>What should change?
          <textarea className="textarea" rows={3} value={request} disabled={advancing} maxLength={2000}
            data-testid="revision-request" onChange={event => setRequest(event.target.value)} />
        </label>}
        <button type="submit" className="btn btn--primary" data-testid="revision-submit"
          disabled={advancing || building || (!rebuild && !request.trim())}>
          {rebuild ? 'Rebuild with the new builder' : 'Update my game'}
        </button>
      </form>}
    {ended && <div role="alert" data-testid="revision-ended">
      <p>{stopped ?? (run?.failure?.kind === 'temporary' ? CHANGE_BUSY_MESSAGE : CHANGE_FAILED_MESSAGE)}</p>
      {stopped === null && <AdminErrorDetail detail={run?.failure?.message ?? failure?.message ?? ''} />}
      <button type="button" className="btn" onClick={startOver} data-testid="revision-start-over">Start a new change</button>
    </div>}
    {showProgress && !buildCurrent && <WizardStoryReel identity={creation.id} title={creation.title}
      filmEnabled={creationFilmsEnabled}
      concept={creation.concept ?? creation.title} referenceUrls={[]} stage={stageError ? 'failed' : 'analysis'} chapter="Plan" running={!stageError}
      activity={run.request ? `Making your change: ${run.request.split('\n')[0]}` : composedRevisionStages(run, runRebuild, advancing).find(stage => stage.status === 'running')?.label ?? 'Preparing your update'}
      scopeChanges={run.plan?.notIncluded} design={creation.gameDesign} actions={failure?.kind === 'retry-limit' ? <ComposedRetryLimit message={RETRY_LIMIT_CHANGE_MESSAGE}><button type="button" className="btn" onClick={startOver} data-testid="revision-start-over">Start a new change</button></ComposedRetryLimit> : stageError ? <><ComposedRunFailure stageLabel={composedRevisionStages(run, runRebuild, advancing).find(stage => stage.id === run.stage)?.label} detail={stageError} onRetry={() => retry(run)} /><button type="button" className="btn" onClick={startOver} data-testid="revision-start-over">Start a new change</button></> : undefined}
      details={<ComposedCreateProgress plan={run.plan ?? null} templateTitle={composedPlanTemplateTitleV1(run.plan ?? null)}
        stages={composedRevisionStages(run, runRebuild, advancing)} result={null} />} />}

    {!showProgress && !ended && failure && <div role="alert" data-testid="revision-error">{failure.plain}<AdminErrorDetail detail={failure.message} /></div>}
    {buildCurrent && <p role="status" data-testid="revision-accepted">
      {live
        ? `Version ${builtVersion} is live.`
        : buildFailed
          ? `Version ${builtVersion} did not finish building. Your live game is unchanged.`
          : `Building version ${builtVersion}. Its progress shows below.`}
    </p>}
    {buildRetryable && <div data-testid="revision-build-retry">
      <p>Repairing builds this change again from its saved plan: it keeps the finished art and does not rewrite the game.</p>
      <button type="button" className="btn btn--primary" disabled={advancing || building}
        onClick={() => retryBuild(buildFailed)}>{repairing ? 'Repairing…' : 'Repair saved build'}</button>
      {buildRetryError && <div role="alert">{buildRetryError.plain}<AdminErrorDetail detail={buildRetryError.detail} /></div>}
    </div>}
    {live && run && <details data-testid="revision-results"><summary>What changed in this version</summary>
      {run.changes?.length
        ? <div data-testid="revision-changes"><h4>What changed</h4><ul>{run.changes.map(line => <li key={line}>{plainPlanText(line)}</li>)}</ul></div>
        : runRebuild && <p data-testid="revision-changes">Your game was rebuilt with the new builder and plays as before.</p>}
      {run.art && run.art.kept.length > 0 && <div data-testid="revision-art-kept"><h4>Art kept</h4>
        <ul>{run.art.kept.map(({ slot, reason }) => <li key={slot}>{slot} — {COMPOSED_REVISION_KEPT_REASON[reason]}</li>)}</ul></div>}
      {run.art && run.art.redrawn.length > 0 && <div data-testid="revision-art-redrawn"><h4>Art redrawn</h4>
        <ul>{run.art.redrawn.map(slot => <li key={slot}>{slot}</li>)}</ul></div>}
    </details>}
  </section>;
}
